import React, { useRef } from 'react';
import { Modal, Button, message, QRCode } from 'antd-v5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { queryMaker } from '../../../../../core/apiRequests';

/**
 * EventLinksModal component for displaying event booking links and QR codes
 */
// Sanitize event name for use as filename
const sanitizeFilename = name => {
  if (!name) return 'event';
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

const EventLinksModal = ({ open, onClose, event, auth }) => {
  const qrRef = useRef(null);

  // Generate booking URL (kiosk URL with eventId)
  const kioskUrl = `${process.env.REACT_APP_OPT_IN_FORM_URL}${queryMaker({
    merchantUuid: auth?.uuid,
    type: 'event',
    eventId: event?.id,
  })}`;

  const handleCopyBookingLink = () => {
    navigator.clipboard
      .writeText(kioskUrl)
      .then(() => {
        message.success('Booking link copied to clipboard');
      })
      .catch(() => {
        message.error('Failed to copy link');
      });
  };

  const handleDownloadQR = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (canvas) {
      const link = document.createElement('a');
      link.download = `${sanitizeFilename(event?.name)}-qrcode.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      message.success('QR code downloaded');
    } else {
      message.error('Failed to download QR code');
    }
  };

  return (
    <Modal
      title="Event Links"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={500}
      className="event-links-modal"
    >
      {/* Booking Link Section (Kiosk URL with eventId) */}
      <div className="link-section">
        <div className="link-label">Booking Link</div>
        <div className="link-value">
          <div className="link-url">{kioskUrl}</div>
          <Button type="primary" icon={<FontAwesomeIcon icon={['fal', 'copy']} />} onClick={handleCopyBookingLink}>
            Copy
          </Button>
        </div>
      </div>

      {/* QR Code Section */}
      <div className="link-section">
        <div className="link-label">QR Code</div>
        <div className="qr-code-section" ref={qrRef}>
          <QRCode value={kioskUrl || 'https://clientbook.com'} size={200} />
          <Button
            type="default"
            icon={<FontAwesomeIcon icon={['fal', 'download']} />}
            onClick={handleDownloadQR}
            style={{ marginTop: 16 }}
          >
            Download QR Code
          </Button>
        </div>
      </div>
    </Modal>
  );
};

EventLinksModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  auth: PropTypes.shape({
    uuid: PropTypes.string,
    jwtToken: PropTypes.string,
  }),
};

EventLinksModal.defaultProps = {
  open: false,
  event: {},
  auth: {},
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(EventLinksModal);
